import React, { useContext, useEffect } from 'react';

import { hot } from 'react-hot-loader/root';

import { TrackEventContext } from 'common/containers/EventContainer';
import Document360LandingHelmet from 'common/helmets/Document360LandingHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import UppercaseHeader from 'common/UppercaseHeader';
import LandingCaseStudyHeadingText from 'common/www/LandingCaseStudyHeadingText';
import Document360Wordmark from 'img/landing/document360-wordmark.webp';
import Saravana from 'img/landing/saravana.webp';

import 'css/components/www/_LandingCustomersPage.scss';

const Document360Landing = () => {
  const trackEvent = useContext(TrackEventContext);

  useEffect(() => {
    trackEvent('Viewed Document360 Case Study');
  }, [trackEvent]);

  return (
    <div className="landingDocument360 landingCustomersPage">
      <Document360LandingHelmet />
      <div className="fullWidthContainer">
        <div className="header">
          <LandingCaseStudyHeadingText title="How Document360 uses Canny to be customer-obsessed and close new business" />
          <div className="headerImage">
            <div className="nametag">
              <div className="name">Saravana Kumar</div>
              <div className="title">Founder, CEO at Kovai</div>
            </div>
            <LazyLoadedImage loading="eager" src={Saravana} alt="Saravana Kumar" />
          </div>
        </div>
      </div>
      <div className="mainContent">
        <div className="mainContentText">
          <div className="left">
            <p>
              Document360 is one of{' '}
              <a href="http://kovai.co" target="_blank" rel="noreferrer">
                Kovai.co
              </a>
              ’s flagship products. Kovai is dedicated to building great and scalable SaaS products.
              Saravana Kumar, the founder, built a large and growing company and was looking for new
              ways of delighting their&nbsp;customers.
            </p>
            <h2 className="sectionTitle">Challenge</h2>
            <p>
              As the team at Kovai.co was building a new product called Document360, they were
              getting a lot of engagement and ideas from their existing user base. This is‌ a good
              problem to have. But, it’s still a challenge. The team couldn’t keep up with the
              requests, process them quickly enough, and find the best&nbsp;ideas.
            </p>
            <p>
              There were a lot of feature requests coming from sales and customer success.
              Document360 team started managing them with Excel – they would manually input each
              feature request and color code them. It worked…until it&nbsp;didn’t.
            </p>
            <p className="quote">
              &ldquo;It’s OK when you have 50 customers. But, when we started to see phenomenal
              growth, the number of feature requests dramatically increased too. We couldn’t manage
              it anymore. What’s more, we couldn’t keep our users updated. Looking for each feature
              request and its author in CRM was just too cumbersome.&rdquo;
            </p>
            <p>Overall, they faced two major problems:</p>
            <ol className="numberedOrderedList">
              <li>Identifying similar feature requests</li>
              <li>Notifying users when the feature they requested is shipped</li>
            </ol>
            <h2 className="sectionTitle">Solution</h2>
            <p>Their team evaluated many tools before they landed on&nbsp;Canny.</p>
            <p className="quote">
              &ldquo;Canny came out on top for two reasons: it’s very simple to use, and our users
              love&nbsp;it.&rdquo;
            </p>
            <p>
              Saravana and his team commented that Canny’s interface looked familiar from the very
              first time they logged&nbsp;in.
            </p>
            <p className="quote">
              &ldquo;I don’t think you can build a product as intuitive as&nbsp;Canny.&rdquo;
            </p>
            <p>
              The Document360 team also uses Canny internally – to build their product roadmap out
              of winning feature&nbsp;requests.
            </p>
            <p className="quote">
              &ldquo;We also love the analytics inside of Canny. We can see which posts are popular
              and trending, and we use that data to prioritize feature requests. We categorize ideas
              into “feature requests”, “bugs”, “feature enhancements”, and so on. Another thing
              that’s much easier in&nbsp;Canny!&rdquo;
            </p>
            <p>
              Canny helps Kovai stay true to its values. One of them is
              being&nbsp;customer-obsessed.
            </p>
            <p className="quote">
              &ldquo;We read every comment and request, we communicate with our users, and they
              notice&nbsp;it.&rdquo;
            </p>
            <p>
              Canny’s ability to integrate Microsoft Teams is another important aspect for Kovai’s
              team. They track new requests in Teams and connect them to Microsoft Power BI where
              product managers analyze the&nbsp;trends.
            </p>
            <p className="quote">
              &ldquo;We’re a data-driven company, and Canny helps us make
              smart&nbsp;decisions.&rdquo;
            </p>
            <h2 className="sectionTitle">Results</h2>
            <p>
              Another of Kovai’s cultural values is honesty. Canny helps the organization deliver on
              that promise. Their users can see that the team listens, takes user feedback to heart,
              and actually builds what they ask&nbsp;for.
            </p>
            <p className="quote">
              &ldquo;Our Canny roadmap is public, so the whole world can see what we’re building. We
              believe that it adds credibility and instills&nbsp;trust.&rdquo;
            </p>
            <p>
              Their customer satisfaction has gone up since they implemented Canny. What’s more, ‌
              customers view the company as transparent, and many choose their software because of
              that. So, Canny indirectly helps Document360 win new&nbsp;business!
            </p>
            <p>
              Because the roadmap is public, users can also see how quickly each comment is
              addressed and how long each feature’s development takes. This makes the team
              accountable and also helps&nbsp;sales.
            </p>
            <p>
              Kovai.co also uses internal boards within Canny. That’s where the team brainstorms and
              discusses different ideas. It’s very convenient to have all of their ideas in
              one&nbsp;place.
            </p>
            <p>
              Finally, Canny saves Document360 a lot of time. Instead of trying to manage all their
              feedback in a spreadsheet, the team can now automate most of their feedback management
              in&nbsp;Canny.
            </p>
            <p>
              We’re happy to support businesses like Kovai and can’t wait to see what’s next
              for&nbsp;them.
            </p>
            <p>
              If you’d also like to increase productivity, delight your customers, and win new
              business,{' '}
              <a target="_blank" href="https://canny.io/register" rel="noopener">
                check out Canny!
              </a>
            </p>
          </div>
          <div className="right">
            <LazyLoadedImage
              className="Document360Wordmark"
              src={Document360Wordmark}
              alt="Document360 Wordmark"
            />
            Document360 is a SaaS platform that helps create a self-service knowledge base for
            enterprise and software&nbsp;projects.
            <a href="https://document360.com/" rel="noopener" target="_blank">
              document360.com
            </a>
            <div className="stat">
              <UppercaseHeader>Employees</UppercaseHeader>
              <p>290+</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Founded</UppercaseHeader>
              <p>2011</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Using Canny since</UppercaseHeader>
              <p>2018</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Votes on Canny</UppercaseHeader>
              <p>13,450</p>
            </div>
          </div>
        </div>
        <LandingFooterCTA style="solid" expanded={true} />
      </div>
    </div>
  );
};

export default hot(Document360Landing);
